/**
 * Assertion entity: backend DTOs, API calls, and the DTO -> display mapper.
 *
 * Assertions are compliance points reeve can't read from documents — the user
 * confirms them ("the H&S poster is up"). The category enum is the same six-way
 * DocCategory as documents.
 */

import type { CompanyAssertion } from '../../app/types';
import { apiGet, apiPost } from '../apiClient';
import { categoryToKebab, formatDate } from './format';

/** Backend assertion category enum (camelCase on the wire). */
export type AssertionCategory =
  | 'legallyRequired'
  | 'essential'
  | 'bestPractice'
  | 'optional'
  | 'notRelevant'
  | 'shouldNotHave';

export interface AssertionDto {
  id: string;
  companyId: string;
  assertionTypeId: string | null;
  statement: string;
  why: string;
  category: AssertionCategory;
  confirmed: boolean;
  confirmedAt: string | null;
  confirmedByUserId: string | null;
  expiresOn: string | null;
  note: string | null;
  createdAt: string;
  lastUpdated: string;
}

export interface CreateAssertionRequest {
  companyId: string;
  assertionTypeId?: string;
  statement: string;
  why: string;
  category: AssertionCategory;
}

/** GET /api/assertions?companyId={id}. */
export function listAssertions(companyId: string): Promise<AssertionDto[]> {
  return apiGet<AssertionDto[]>(`/api/assertions?companyId=${encodeURIComponent(companyId)}`);
}

/** POST /api/assertions. */
export function createAssertion(request: CreateAssertionRequest): Promise<AssertionDto> {
  return apiPost<AssertionDto>('/api/assertions', request);
}

/** POST /api/assertions/{id}/confirm — set or clear the confirmation. */
export function confirmAssertion(id: string, confirmed: boolean): Promise<AssertionDto> {
  return apiPost<AssertionDto>(`/api/assertions/${id}/confirm`, { confirmed });
}

/** AssertionDto -> display CompanyAssertion. */
export function mapAssertion(dto: AssertionDto): CompanyAssertion {
  return {
    id: dto.id,
    statement: dto.statement,
    why: dto.why,
    category: categoryToKebab(dto.category),
    confirmed: dto.confirmed,
    confirmedOn:
      dto.confirmed && dto.confirmedAt ? `Confirmed ${formatDate(dto.confirmedAt)}` : undefined,
    note: dto.note ?? undefined,
  };
}
